/**
 * ViewScopeContext — spec 2026-07-18 + fix 2026-07-24 (bug #1).
 *
 * Le toggle « Vue globale » (dans Tâches et Documents) doit :
 *   - persister quand on navigue entre modules (Tâches ↔ Documents)
 *   - persister quand on visite un détail puis revient
 *   - persister même après un passage en verrouillage (lock)
 *   - être remis à « mine » lors d'une nouvelle connexion (login/logout)
 *   - être remis à « mine » à l'ouverture d'un nouvel onglet/nouvelle session
 *
 * Fix bug #1 (2026-07-24) : le toggle ne bougeait pas dans certains cas
 * après lock/déverrouillage. On adosse maintenant l'état à sessionStorage
 * pour survivre à un reload de page (le lock n'est plus un unmount total
 * mais garantit la persistance de l'état), tout en repartant à zéro sur
 * un nouvel onglet.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const ViewScopeContext = createContext(null);
const STORAGE_KEY = "ledesk_view_scope";

// Point 3 (2026-09) — valeurs : "mine" | "global" | "user:<id>". Mémorisé PAR utilisateur (localStorage).
const _key = (uid) => (uid ? `${STORAGE_KEY}:${uid}` : STORAGE_KEY);
const _valid = (v) => v === "global" || v === "mine" || (typeof v === "string" && v.startsWith("user:"));
const _read = (uid) => {
  try { const v = localStorage.getItem(_key(uid)) || sessionStorage.getItem(STORAGE_KEY); return _valid(v) ? v : "mine"; } catch { return "mine"; }
};
const _write = (v, uid) => {
  try {
    if (v && v !== "mine") localStorage.setItem(_key(uid), v); else localStorage.removeItem(_key(uid));
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
};
export const viewAsUserId = (scope) => (typeof scope === "string" && scope.startsWith("user:") ? scope.slice(5) : null);

export function ViewScopeProvider({ children }) {
  const { user } = useAuth();
  // Hydrate depuis sessionStorage — permet à la vue de survivre à un
  // reload/lock. Nouvel onglet = sessionStorage vide = "mine".
  const [viewScope, _setViewScope] = useState(() => _read(user?.id));
  const prevUserIdRef = useRef(user?.id || null);

  const setViewScope = useCallback((next) => {
    const v = _valid(next) ? next : "mine";
    _write(v, user?.id);
    _setViewScope(v);
  }, [user?.id]);

  // Reset uniquement lors d'un vrai swap d'identité (logout → login autre
  // compte, ou déconnexion). Ne se déclenche pas au premier hydration
  // (null → real user id) — sinon on effacerait sessionStorage à chaque
  // reload de page.
  useEffect(() => {
    const uid = user?.id || null;
    const prev = prevUserIdRef.current;
    // Cas #1 : première hydration (prev est null) — on capture juste
    // l'id sans réinitialiser le contexte.
    // Cas #2 : logout (uid null, prev non-null) → reset.
    // Cas #3 : login sur un autre compte (les deux non-null et différents)
    //          → reset.
    if (uid !== prev) {
      _setViewScope(_read(uid));
    }
    prevUserIdRef.current = uid;
  }, [user?.id, setViewScope]);

  return (
    <ViewScopeContext.Provider value={{ viewScope, setViewScope }}>
      {children}
    </ViewScopeContext.Provider>
  );
}

export function useViewScope() {
  const ctx = useContext(ViewScopeContext);
  if (!ctx) return { viewScope: "mine", setViewScope: () => {} };
  return ctx;
}
